import DOMPurify from 'dompurify';
import { newContentId } from '../../../services/TemplateUtils';
import type { TableCell, TableCellStyle, TableRow } from '../../template/Table';
import { cellRichText, collectStyleRules } from './pastedTableRichText';

/**
 * Model layer for the v2 Table editor. The `TableContent.rows` JSON is the
 * single source of truth; every operation is a pure `rows -> rows` function that
 * runs through {@link normalizeTable} so the result is *always* a valid, exactly
 * rectangular grid (accounting for col/row spans) with no overlaps or holes and
 * no fields the schema can't express. Nothing reads structure back from the DOM.
 */

const ALLOWED_TAGS = ['b', 'i', 'em', 'strong', 'u', 'sub', 'sup', 'br', 'a'];
const ALLOWED_ATTR = ['href', 'target', 'rel'];

export const CELL_STYLES: TableCellStyle[] = ['header', 'subHeader', 'normal'];

/** Cell content is restricted to a small inline whitelist — no block tags, no images. */
export function sanitizeCellHtml(html: string): string {
  return DOMPurify.sanitize(html ?? '', { ALLOWED_TAGS, ALLOWED_ATTR }).trim();
}

/** Header / sub-header cells are bold by style, so they carry no inline emphasis. */
export function sanitizeCellPlain(html: string): string {
  return DOMPurify.sanitize(html ?? '', { ALLOWED_TAGS: ['br', 'a'], ALLOWED_ATTR }).trim();
}

/** The effective style of a cell — an unset cell in row 0 still reads as a header. */
export function resolveCellStyle(cell: TableCell, rowIndex: number): TableCellStyle {
  return cell.style ?? (rowIndex === 0 ? 'header' : 'normal');
}

function styledCell(cell: TableCell, style: TableCellStyle): TableCell {
  if (style === 'normal') {
    const { style: _dropped, ...rest } = cell;
    return rest;
  }
  return { ...cell, style };
}

export function cellText(cell: TableCell): string {
  const content = cell.content;
  if (Array.isArray(content)) return content.map((entry) => entry.content).join(' ');
  return content?.content ?? '';
}

function withHtml(cell: TableCell, html: string): TableCell {
  const existing = Array.isArray(cell.content) ? cell.content[0] : cell.content;
  return {
    ...cell,
    content: { id: existing?.id ?? newContentId(), content: sanitizeCellHtml(html) },
  };
}

export function emptyCell(): TableCell {
  return { id: newContentId(), content: { id: newContentId(), content: '' } };
}

/** A cell holding a single trimmed, inline-sanitised string — for text/heuristic imports. */
export function cellFromText(value: string): TableCell {
  return {
    id: newContentId(),
    content: { id: newContentId(), content: sanitizeCellHtml((value ?? '').trim()) },
  };
}

const span = (value?: number): number => Math.max(1, Math.floor(value ?? 1));

/** Column count a row occupies (sum of its cells' colSpans). */
function rowWidth(row: TableRow): number {
  return row.cells.reduce((sum, cell) => sum + span(cell.colSpan), 0);
}

// ---------------------------------------------------------------------------
// Dense grid <-> sparse rows. Merged cells share one object across every slot
// they cover, so array splices on the grid handle insert/delete correctly.
// ---------------------------------------------------------------------------

type Grid = TableCell[][];

export function toGrid(rows: TableRow[]): Grid {
  const grid: Grid = [];
  const taken = (r: number, c: number) => grid[r]?.[c] != null;

  rows.forEach((row, r) => {
    if (!grid[r]) grid[r] = [];
    let c = 0;
    row.cells.forEach((cell) => {
      while (taken(r, c)) c += 1;
      const cs = span(cell.colSpan);
      const rs = span(cell.rowSpan);
      for (let dr = 0; dr < rs; dr += 1) {
        for (let dc = 0; dc < cs; dc += 1) {
          if (!grid[r + dr]) grid[r + dr] = [];
          grid[r + dr][c + dc] = cell;
        }
      }
      c += cs;
    });
  });

  const width = Math.max(0, ...grid.map((row) => row.length));
  grid.forEach((row) => {
    for (let c = 0; c < width; c += 1) if (row[c] == null) row[c] = emptyCell();
  });
  return grid;
}

export function fromGrid(grid: Grid): TableRow[] {
  const seen = new Set<TableCell>();
  return grid.map((row, r) => ({
    cells: row.flatMap((cell, c) => {
      if (seen.has(cell)) return [];
      seen.add(cell);
      let colSpan = 1;
      while (row[c + colSpan] === cell) colSpan += 1;
      let rowSpan = 1;
      while (grid[r + rowSpan]?.[c] === cell) rowSpan += 1;
      const out: TableCell = { ...cell, id: cell.id || newContentId() };
      if (colSpan > 1) out.colSpan = colSpan;
      else delete out.colSpan;
      if (rowSpan > 1) out.rowSpan = rowSpan;
      else delete out.rowSpan;
      return [out];
    }),
  }));
}

export function normalizeTable(rows: TableRow[]): TableRow[] {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  return fromGrid(toGrid(rows));
}

// ---------------------------------------------------------------------------
// Operations — each returns normalized rows.
// ---------------------------------------------------------------------------

export function tableSize(rows: TableRow[]): { rowCount: number; columnCount: number } {
  const grid = toGrid(rows);
  return { rowCount: grid.length, columnCount: grid[0]?.length ?? 0 };
}

export function createEmptyTable(columns = 3, rowsCount = 3): TableRow[] {
  return Array.from({ length: rowsCount }, (_row, r) => ({
    cells: Array.from({ length: columns }, () =>
      r === 0 ? { ...emptyCell(), style: 'header' as TableCellStyle } : emptyCell(),
    ),
  }));
}

export function setCellContent(rows: TableRow[], r: number, c: number, html: string): TableRow[] {
  const grid = toGrid(rows);
  const target = grid[r]?.[c];
  if (!target) return rows;
  const clean = target.style && target.style !== 'normal' ? sanitizeCellPlain(html) : html;
  const updated = withHtml(target, clean);
  return fromGrid(grid.map((row) => row.map((cell) => (cell === target ? updated : cell))));
}

/** Set the style of every cell anchored in row `r`. */
export function setRowStyle(rows: TableRow[], r: number, style: TableCellStyle): TableRow[] {
  const grid = toGrid(rows);
  if (!grid[r]) return rows;
  const replace = new Map<TableCell, TableCell>();
  grid[r].forEach((cell) => {
    if (!replace.has(cell)) replace.set(cell, styledCell(cell, style));
  });
  return fromGrid(grid.map((row) => row.map((cell) => replace.get(cell) ?? cell)));
}

/** Set the style of a single cell (all slots of a merged cell move together). */
export function setCellStyle(
  rows: TableRow[],
  r: number,
  c: number,
  style: TableCellStyle,
): TableRow[] {
  const grid = toGrid(rows);
  const target = grid[r]?.[c];
  if (!target) return rows;
  const updated = styledCell(target, style);
  return fromGrid(grid.map((row) => row.map((cell) => (cell === target ? updated : cell))));
}

export function insertRow(rows: TableRow[], at: number): TableRow[] {
  const grid = toGrid(rows);
  const width = grid[0]?.length ?? 1;
  const index = Math.max(0, Math.min(at, grid.length));
  const inserted = Array.from({ length: width }, emptyCell);
  if (index > 0 && index < grid.length) {
    for (let c = 0; c < width; c += 1) {
      if (grid[index - 1][c] === grid[index][c]) inserted[c] = grid[index - 1][c];
    }
  }
  grid.splice(index, 0, inserted);
  return fromGrid(grid);
}

export function deleteRow(rows: TableRow[], at: number): TableRow[] {
  const grid = toGrid(rows);
  if (grid.length <= 1) return rows;
  grid.splice(at, 1);
  return fromGrid(grid);
}

export function insertColumn(rows: TableRow[], at: number): TableRow[] {
  const grid = toGrid(rows);
  grid.forEach((row) => {
    const spans = at > 0 && at < row.length && row[at - 1] === row[at];
    row.splice(at, 0, spans ? row[at - 1] : emptyCell());
  });
  return fromGrid(grid);
}

export function deleteColumn(rows: TableRow[], at: number): TableRow[] {
  const grid = toGrid(rows);
  if ((grid[0]?.length ?? 0) <= 1) return rows;
  grid.forEach((row) => row.splice(at, 1));
  return fromGrid(grid);
}

/** Expand a rectangle so it never partially covers a merged cell. */
function expandRect(
  grid: Grid,
  rect: [number, number, number, number],
): [number, number, number, number] {
  const bounds = { r1: rect[0], c1: rect[1], r2: rect[2], c2: rect[3] };
  let changed = true;
  while (changed) {
    changed = false;
    const covered = new Set<TableCell>();
    for (let r = bounds.r1; r <= bounds.r2; r += 1) {
      for (let c = bounds.c1; c <= bounds.c2; c += 1) {
        if (grid[r]?.[c]) covered.add(grid[r][c]);
      }
    }
    grid.forEach((row, rr) => {
      row.forEach((cell, cc) => {
        if (!covered.has(cell)) return;
        if (rr < bounds.r1 || rr > bounds.r2 || cc < bounds.c1 || cc > bounds.c2) changed = true;
        bounds.r1 = Math.min(bounds.r1, rr);
        bounds.r2 = Math.max(bounds.r2, rr);
        bounds.c1 = Math.min(bounds.c1, cc);
        bounds.c2 = Math.max(bounds.c2, cc);
      });
    });
  }
  return [bounds.r1, bounds.c1, bounds.r2, bounds.c2];
}

export function mergeCells(rows: TableRow[], a: [number, number], b: [number, number]): TableRow[] {
  const grid = toGrid(rows);
  const rect = expandRect(grid, [
    Math.min(a[0], b[0]),
    Math.min(a[1], b[1]),
    Math.max(a[0], b[0]),
    Math.max(a[1], b[1]),
  ]);
  const [r1, c1, r2, c2] = rect;
  if (r1 === r2 && c1 === c2) return rows;

  const anchor = grid[r1][c1];
  const pieces: string[] = [];
  const consumed = new Set<TableCell>([anchor]);
  for (let r = r1; r <= r2; r += 1) {
    for (let c = c1; c <= c2; c += 1) {
      const cell = grid[r][c];
      if (consumed.has(cell)) continue;
      consumed.add(cell);
      const text = cellText(cell).trim();
      if (text) pieces.push(text);
    }
  }
  const merged = withHtml(
    anchor,
    [cellText(anchor).trim(), ...pieces].filter(Boolean).join('<br>'),
  );
  return fromGrid(
    grid.map((row, r) =>
      row.map((cell, c) => {
        if (r >= r1 && r <= r2 && c >= c1 && c <= c2) return merged;
        return cell === anchor ? merged : cell;
      }),
    ),
  );
}

export function splitCell(rows: TableRow[], r: number, c: number): TableRow[] {
  const grid = toGrid(rows);
  const target = grid[r]?.[c];
  if (!target) return rows;
  let keptOrigin = false;
  return fromGrid(
    grid.map((row) =>
      row.map((cell) => {
        if (cell !== target) return cell;
        if (!keptOrigin) {
          keptOrigin = true;
          return target;
        }
        return emptyCell();
      }),
    ),
  );
}

function parseHtmlTableCell(td: Element, rules: Map<string, string>): TableCell {
  const cell = emptyCell();
  cell.content = {
    id: newContentId(),
    content: cellRichText(td, rules, sanitizeCellHtml) || (td.textContent ?? '').trim(),
  };
  const cs = parseInt(td.getAttribute('colspan') ?? '1', 10);
  const rs = parseInt(td.getAttribute('rowspan') ?? '1', 10);
  if (cs > 1) cell.colSpan = cs;
  if (rs > 1) cell.rowSpan = rs;
  if (td.tagName.toLowerCase() === 'th') cell.style = 'header';
  return cell;
}

/** Parse a pasted HTML table or a TSV block into normalized rows, or null. */
export function parseClipboardTable(html: string, text: string): TableRow[] | null {
  if (html && /<table[\s>]/i.test(html)) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const table = doc.querySelector('table');
    if (table) {
      const rules = collectStyleRules(doc);
      const parsed: TableRow[] = Array.from(table.querySelectorAll('tr')).map((tr) => ({
        cells: Array.from(tr.querySelectorAll('th,td')).map((td) => parseHtmlTableCell(td, rules)),
      }));
      const width = Math.max(1, ...parsed.map((row) => rowWidth(row)));
      parsed.forEach((row, r) => {
        // A lone cell spanning the full width below the header row is a section divider.
        if (r > 0 && row.cells.length === 1 && (row.cells[0].colSpan ?? 1) >= width) {
          if (!row.cells[0].style) row.cells[0].style = 'subHeader';
        }
      });
      if (parsed.some((row) => row.cells.length)) return normalizeTable(parsed);
    }
  }

  const lines = (text ?? '')
    .replace(/\r/g, '')
    .split('\n')
    .filter((line) => line.length > 0);
  if (lines.length >= 1 && lines.some((line) => line.includes('\t'))) {
    return normalizeTable(
      lines.map((line) => ({
        cells: line.split('\t').map((value) => withHtml(emptyCell(), value)),
      })),
    );
  }
  return null;
}

/**
 * A copied single spreadsheet cell still arrives as `<table><tr><td>…</td></tr></table>` —
 * genuinely table-shaped, but not a multi-cell paste, so it should fill the focused cell
 * rather than trigger the whole-table replace flow. Returns that cell's HTML, or null when
 * the clipboard holds more than one cell (defer to the caller's table-replace handling) or
 * isn't table-shaped at all (defer to the browser's own paste-into-contenteditable).
 */
export function singleCellClipboardValue(html: string, text: string): string | null {
  const parsed = parseClipboardTable(html, text);
  if (!parsed) return null;
  const totalCells = parsed.reduce((n, row) => n + row.cells.length, 0);
  if (parsed.length !== 1 || totalCells !== 1) return null;
  return cellText(parsed[0].cells[0]);
}

export interface AnchorCell {
  r: number;
  c: number;
  cell: TableCell;
  colSpan: number;
  rowSpan: number;
}

/** Per-row list of the cells whose top-left slot lives in that row (for rendering). */
export function gridAnchors(grid: TableCell[][]): AnchorCell[][] {
  const seen = new Set<TableCell>();
  return grid.map((row, r) =>
    row.flatMap((cell, c) => {
      if (seen.has(cell)) return [];
      seen.add(cell);
      let colSpan = 1;
      while (row[c + colSpan] === cell) colSpan += 1;
      let rowSpan = 1;
      while (grid[r + rowSpan]?.[c] === cell) rowSpan += 1;
      return [{ r, c, cell, colSpan, rowSpan }];
    }),
  );
}
